import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  eatDayOf,
  formatIsoDay,
  REFERRAL_DIRECTION_LABELS,
  REFERRAL_URGENCY_LABELS,
  REFERRAL_URGENCIES,
  type ReferralUrgency,
} from '@wodi/domain';
import {
  Button,
  DateField,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  RadioGroup,
  Section,
  Tag,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { patientBannerQuery } from '../api/patients.ts';
import { logIncomingReferral, patientReferralsQuery, referralKeys } from '../api/referrals.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { formatInstant } from '../patients/format.ts';
import { toasts } from '../toasts.ts';
import { FacilityPicker, facilityFields, type FacilityChoice } from './facility-picker.tsx';
import { referralStatusTag } from './labels.ts';
import styles from './referrals.module.css';

// Referrals on the patient record (docs/plans/phase2-referrals.md §1.1 items 4 and 8): every
// referral of the patient, sent and received, and "Log referral received" at reception on
// the patient's open visit here. What their letter says is typed as written; the paper is
// uploaded under Documents as "Referral letter received".

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function LogReferralDialog({
  patientId,
  visitId,
  mayRegistrySearch,
  onClose,
}: {
  patientId: string;
  visitId: string;
  mayRegistrySearch: boolean;
  onClose: () => void;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [facility, setFacility] = useState<FacilityChoice | null>(null);
  const [clinician, setClinician] = useState('');
  const [reference, setReference] = useState('');
  const [letterDate, setLetterDate] = useState<string | null>(null);
  const [urgency, setUrgency] = useState<ReferralUrgency>('routine');
  const [reason, setReason] = useState('');
  const [summary, setSummary] = useState('');
  const [tried, setTried] = useState(false);
  const from = facilityFields(facility);
  const facilityError =
    from.name === '' ? 'Choose the facility that referred the patient, or type its name.' : null;
  const reasonError =
    reason.trim() === '' ? 'Type the reason for the referral as their letter gives it.' : null;
  const optional = (text: string) => (text.trim() === '' ? null : text.trim());
  const save = useMutation({
    mutationFn: () =>
      logIncomingReferral(visitId, {
        clientRequestId,
        registryFacilityId: from.registryFacilityId,
        fromFacilityName: from.name,
        fromFacilityMflCode: from.mflCode,
        referringClinician: optional(clinician),
        externalReference: optional(reference),
        theirLetterDate: letterDate,
        urgency,
        reason: reason.trim(),
        summaryText: optional(summary),
      }),
    onSuccess: async (referral) => {
      await queryClient.invalidateQueries({ queryKey: referralKeys.patient(patientId) });
      await queryClient.invalidateQueries({ queryKey: referralKeys.all });
      toasts.show({ title: 'Referral logged' });
      onClose();
      void navigate({ to: '/referrals/$referralId', params: { referralId: referral.id } });
    },
  });
  return (
    <Dialog
      title="Log referral received"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <p className={styles.meta}>
          Type what their letter says. Upload the letter itself under Documents, as &quot;Referral
          letter received&quot;.
        </p>
        <FacilityPicker
          label="Referring facility"
          value={facility}
          onChange={setFacility}
          mayRegistrySearch={mayRegistrySearch}
          errorMessage={tried && facilityError !== null ? facilityError : undefined}
        />
        <div className={styles.row}>
          <TextField
            label="Referring clinician (optional)"
            value={clinician}
            onChange={setClinician}
            maxLength={200}
          />
          <TextField
            label="Their reference (optional)"
            description="The number on their letter."
            value={reference}
            onChange={setReference}
            maxLength={60}
          />
          <DateField
            label="Their letter dated (optional)"
            value={letterDate}
            onChange={setLetterDate}
            maxValue={formatIsoDay(eatDayOf(new Date()))}
          />
        </div>
        <RadioGroup
          label="Urgency"
          value={urgency}
          onChange={(v) => {
            setUrgency(v as ReferralUrgency);
          }}
          options={REFERRAL_URGENCIES.map((id) => ({ id, label: REFERRAL_URGENCY_LABELS[id] }))}
        />
        <TextArea
          label="Reason for referral"
          value={reason}
          onChange={setReason}
          rows={3}
          isRequired
          {...(tried && reasonError !== null ? { errorMessage: reasonError } : {})}
        />
        <TextArea
          label="Summary from their letter (optional)"
          value={summary}
          onChange={setSummary}
          rows={3}
        />
        {save.isError ? (
          <Notice tone="error">{errorText(save.error, "The referral couldn't be logged.")}</Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!online}
          isPending={save.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            setTried(true);
            if (facilityError !== null || reasonError !== null) return;
            save.mutate();
          }}
        >
          Log referral
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function PatientReferralsSection({ patientId }: { patientId: string }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const [logging, setLogging] = useState(false);
  const mayRead = session !== null && can(session, 'referrals.read');
  const mayLog = session !== null && can(session, 'referrals.incoming.record');
  const mayRegistrySearch = session !== null && can(session, 'registries.search');
  const referrals = useQuery({ ...patientReferralsQuery(patientId), enabled: mayRead });
  const banner = useQuery({ ...patientBannerQuery(patientId), enabled: mayLog });
  if (!mayRead && !mayLog) return null;
  const openVisit = banner.data?.openVisit ?? null;
  return (
    <Section
      title="Referrals"
      description="Referrals sent from here and received here, newest first."
      {...(mayLog
        ? {
            actions: (
              <Button
                dense
                isDisabled={!online || openVisit === null}
                onPress={() => {
                  setLogging(true);
                }}
              >
                Log referral received
              </Button>
            ),
          }
        : {})}
    >
      {mayLog && openVisit === null && banner.isSuccess ? (
        <p className={styles.meta}>
          Check the patient in first: a referral received is logged on the visit it leads to.
        </p>
      ) : null}
      {!mayRead ? null : referrals.isPending ? (
        <LoadingBlock label="Loading referrals" rows={2} />
      ) : referrals.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void referrals.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(referrals.error, "Referrals couldn't be loaded. Try again.")}
        </Notice>
      ) : referrals.data.items.length === 0 ? (
        <p className={styles.meta}>No referrals yet.</p>
      ) : (
        <ul className={styles.letters} aria-label="Referrals">
          {referrals.data.items.map((r) => {
            const tag = referralStatusTag(r.status);
            return (
              <li key={r.id} className={styles.letter}>
                <span>
                  <Link to="/referrals/$referralId" params={{ referralId: r.id }}>
                    {`${REFERRAL_DIRECTION_LABELS[r.direction]}: ${r.direction === 'outgoing' ? 'to' : 'from'} ${r.otherFacility.name}`}
                  </Link>
                  <span className={styles.meta}>
                    {` ${formatInstant(r.referredAt)}, ${REFERRAL_URGENCY_LABELS[r.urgency].toLowerCase()}`}
                  </span>
                </span>
                <Tag state={tag.state}>{tag.word}</Tag>
              </li>
            );
          })}
        </ul>
      )}
      {logging && openVisit !== null ? (
        <LogReferralDialog
          patientId={patientId}
          visitId={openVisit.id}
          mayRegistrySearch={mayRegistrySearch}
          onClose={() => {
            setLogging(false);
          }}
        />
      ) : null}
    </Section>
  );
}
